/**
 * Best-effort cloud sync for progress data. Mirrors the mobile app's
 * settingsService.syncSettingsToCloud() (gateway path) and
 * progressStore.fetchPercentileThresholds(), but every call here is
 * fire-and-forget and fails silently: the local store (store.ts) is always
 * the source of truth for what the UI shows, and this module only ever
 * pushes/pulls in the background on top of it.
 *
 * apps/web/lib/gateway.ts (the web<->mobile gateway bridge) is owned by
 * another agent. gatewayFetchJson is imported lazily inside each function
 * (not at module scope) so this file — and everything that imports it —
 * keeps working even if that module is missing or changes shape in a
 * checkout mid-merge: a missing module, a signed-out user (GatewayAuthError),
 * and a 404 for a route the backend doesn't have yet all just mean "don't
 * sync yet".
 */

import { accountChangedSinceLoad } from '../accountLocalState'
import {
  getProgressState,
  getServerFirstOpen,
  mergeServerProgress,
  setPercentileThresholds,
  type Celebration,
  type PercentileThresholds,
  type ServerProgressFields,
} from './store'

type GatewayFetchJson = <T>(path: string, init?: RequestInit) => Promise<T>

interface GatewayModule {
  gatewayFetchJson?: GatewayFetchJson
  /** The shared GET /me (one request for every reader on the page). */
  fetchGatewayMe?: () => Promise<unknown>
}

async function loadGateway(): Promise<GatewayModule | null> {
  try {
    // Path is relative to apps/web/lib/progress/, so '../gateway' is
    // apps/web/lib/gateway.ts.
    return (await import('../gateway')) as GatewayModule
  } catch {
    return null
  }
}

async function getGatewayFetchJson(): Promise<GatewayFetchJson | null> {
  const mod = await loadGateway()
  return typeof mod?.gatewayFetchJson === 'function' ? mod.gatewayFetchJson : null
}

/**
 * Push the current totals to the gateway. Same shape as the mobile app's
 * POST /settings/sync (services/api/domains.ts syncProgress): the server
 * merges progress fields with MAX so this can never regress another
 * device's progress.
 *
 * firstAppOpen is the one field NOT taken from local state: web must never
 * originate that value (a browser's Date.now() pinned the account-wide
 * member-days value permanently, since the server's "earliest wins" merge
 * treats a first write as authoritative). Only echo it back once
 * pullProgressFromCloud has learned it from the server — exactly what the
 * native app does — and omit it entirely otherwise.
 */
export async function syncProgressToCloud(): Promise<void> {
  try {
    const gatewayFetchJson = await getGatewayFetchJson()
    if (!gatewayFetchJson) return
    // These totals were loaded for the owner this page started with; once
    // another tab has signed someone else in they are not the session's to
    // push, and the server keeps the MAX, so a stray push never comes out.
    if (accountChangedSinceLoad()) return

    const state = getProgressState()
    const serverFirstOpen = getServerFirstOpen()
    await gatewayFetchJson('/settings/sync', {
      method: 'POST',
      body: JSON.stringify({
        totalTranslations: state.totalTranslations,
        lifetimeCharacters: state.lifetimeCharacters,
        highestBadgeUnlocked: state.currentBadge?.milestone ?? 0,
        streakLongest: state.streak.longestStreak,
        ...(serverFirstOpen !== null ? { firstAppOpen: serverFirstOpen } : {}),
      }),
    })
  } catch {
    // Best-effort — local state already reflects the translation either way.
  }
}

/**
 * Pulls this account's server-side totals from GET /me and merges them into
 * the local store (store.ts's mergeServerProgress: max() per counter, server
 * value cached for first_app_open). Web is otherwise localStorage-only and
 * never reads its own numbers back, which is why a fresh browser profile
 * undercounts a real account's activity — this closes that gap the same way
 * the native app's own /me merge does. Never throws: a missing gateway
 * module, a signed-out caller (GatewayAuthError), or a network failure just
 * mean local numbers stay whatever they already were.
 */
export async function pullProgressFromCloud(): Promise<void> {
  try {
    const mod = await loadGateway()
    if (typeof mod?.fetchGatewayMe !== 'function') return
    mergeServerProgress((await mod.fetchGatewayMe()) as ServerProgressFields | null)
  } catch {
    // Best-effort — see module doc comment.
  }
}

/**
 * Best-effort activity ping, same contract as the mobile app's
 * POST /activity (services/activityService.ts touchActivity).
 */
export async function touchActivity(): Promise<void> {
  try {
    const gatewayFetchJson = await getGatewayFetchJson()
    if (!gatewayFetchJson) return
    await gatewayFetchJson('/activity', {
      method: 'POST',
      body: JSON.stringify({ lifecyclePath: 'foreground_return', platform: 'web' }),
    })
  } catch {
    // Best-effort.
  }
}

/**
 * Fetch percentile thresholds (characters needed for top 1% / 5% / 10%) and
 * persist them locally, returning any celebration that newly became due.
 * Returns null (no-op) if the gateway isn't available yet, there aren't
 * enough users for percentile badges to make sense, or the request fails —
 * the percentile banner simply stays hidden until this succeeds.
 */
export async function fetchPercentileThresholds(): Promise<Celebration | null> {
  try {
    const gatewayFetchJson = await getGatewayFetchJson()
    if (!gatewayFetchJson) return null

    const result = await gatewayFetchJson<Partial<PercentileThresholds> | null>('/progress/percentiles')
    if (!result) return null

    const { celebration } = setPercentileThresholds({
      top1: result.top1 ?? null,
      top5: result.top5 ?? null,
      top10: result.top10 ?? null,
    })
    return celebration
  } catch {
    // Best-effort — thresholds stay whatever was last cached locally.
    return null
  }
}
